import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams, GridSearchIcon } from "@mui/x-data-grid";
import { RemoveRedEye } from "@mui/icons-material";
import dayjs from "dayjs";
import CustomDataGrid from "../../../../../components/dataGrid/CustomDatagrid";
import CustomButton from "../../../../../components/ui/CustomButton";
import { colorVariables } from "../../../../../utils/colorVariable";
import { useGetAdjustments } from "../../../hooks/useWarehouse";
import { ADJUSTMENT_TYPES, adjustmentTypeLabel } from "../../../utils/adjustmentTypes";
import { AdjustmentReviewModal } from "./SectionCreation/AdjustmentReviewModal";

type Status = "pending" | "approved" | "rejected" | "cancelled";
type StatusTab = "all" | Status;

const statusColor: Record<Status, "warning" | "success" | "error" | "default"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

interface Props {
  category: "yarn" | "accessory";
  title: string;
}

export const StockAdjustmentList = ({ category, title }: Props) => {
  const [status, setStatus] = useState<StatusTab>("all");
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 });
  const [selected, setSelected] = useState<any | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  // debounce de la recherche
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useGetAdjustments({
    page: pagination.page + 1,
    limit: pagination.pageSize,
    search: {
      category: [category],
      ...(status !== "all" ? { status: [status] } : {}),
      ...(typeFilter ? { adjustment_type: [typeFilter] } : {}),
    },
    sort: "id,DESC",
    global_search: search,
  });

  const rows = data?.data?.data ?? [];
  const total = data?.data?.total ?? 0;

  // garde le modal synchronisé avec la liste rafraîchie
  const selectedRow = selected ? rows.find((r: any) => r.id === selected.id) ?? selected : null;

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "id", headerName: "#", width: 70 },
      { field: "stock_label", headerName: "Stock", flex: 1, minWidth: 110 },
      {
        field: "adjustment_type",
        headerName: "Type",
        flex: 1.5,
        minWidth: 200,
        renderCell: (p: GridRenderCellParams) => adjustmentTypeLabel(p.value),
      },
      { field: "quantity_before", headerName: "Before", flex: 0.7, minWidth: 100, align: "right", headerAlign: "right" },
      { field: "new_quantity", headerName: "New", flex: 0.7, minWidth: 100, align: "right", headerAlign: "right" },
      {
        field: "difference",
        headerName: "Difference",
        flex: 0.8,
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        renderCell: (p: GridRenderCellParams) => {
          const d = Number(p.value);
          return (
            <b style={{ color: d > 0 ? colorVariables.darkGreen : colorVariables.red }}>
              {d > 0 ? "+" : ""}{d}
            </b>
          );
        },
      },
      {
        field: "unit",
        headerName: "Unit",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (p: GridRenderCellParams) => p.value || "-",
      },
      {
        field: "damage_location",
        headerName: "Moved to",
        flex: 1,
        minWidth: 130,
        renderCell: (p: GridRenderCellParams) => p.value ?? "-",
      },
      { field: "requested_by_name", headerName: "Requested by", flex: 1.2, minWidth: 150 },
      {
        field: "requested_at",
        headerName: "Date",
        flex: 1,
        minWidth: 150,
        renderCell: (p: GridRenderCellParams) => dayjs(p.value).format("DD/MM/YYYY HH:mm"),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 110,
        renderCell: (p: GridRenderCellParams) => (
          <Chip size="small" label={p.value} color={statusColor[p.value as Status]} />
        ),
      },
      { field: "reviewed_by_name", headerName: "Reviewed by",align: "center", flex: 1.2, minWidth: 150, renderCell: (p: GridRenderCellParams) => p.value ?? "-", },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (p: GridRenderCellParams) => (
          <Tooltip
            title="Show details"
            placement="left"
            sx={{
              "& .MuiTooltip-tooltip": {
                backgroundColor: colorVariables.primary,
                color: colorVariables.white,
                fontSize: "12px",
              },
            }}
          >
            <RemoveRedEye
              sx={{
                height: 20,
                cursor: "pointer",
                "&:hover": { color: colorVariables.primary },
              }}
              onClick={(e: any) => {
                e?.stopPropagation?.();
                setSelected(p.row);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ color: colorVariables.grayText, mb: 2, textAlign: "center" }}>
        {title.toUpperCase()}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={status}
          onChange={(_, v: StatusTab) => {
            setStatus(v);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        >
          <Tab value="all" label="All" />
          <Tab value="pending" label="Pending" />
          <Tab value="approved" label="Approved" />
          <Tab value="rejected" label="Rejected" />
          <Tab value="cancelled" label="Cancelled" />
        </Tabs>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            ml: "auto",
            pb: 0.5,
          }}
        >
          {(typeFilter || searchInput) && (
            <CustomButton
              text="Clear filters"
              background={colorVariables.gradient_primary}
              onClick={() => {
                setTypeFilter("");
                setSearchInput("");
              }}
            />
          )}

          <TextField
            select
            size="small"
            label="Filter by type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 0 }));
            }}
            sx={{ width: 240 }}
          >
            <MenuItem value="">All types</MenuItem>
            {ADJUSTMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: 260,
              height: 40,
              bgcolor: colorVariables.lightGray,
              borderRadius: 2,
            }}
          >
            <IconButton sx={{ p: "10px" }} aria-label="search">
              <GridSearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search adjustment"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Box>
        </Box>
      </Box>

      <CustomDataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        paginationMode="server"
        rowCount={total}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        onRowClick={(params: any) => setSelected(params.row)}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />

      <AdjustmentReviewModal
        open={!!selected}
        onClose={() => setSelected(null)}
        row={selectedRow}
        category={category}
      />
    </Box>
  );
};